import { useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { notify } from "../../lib/notifications";
import { Approval, ApprovalFormData } from "../../components/approvalsTypes";

export const useApprovals = (projectId: string, contractorId: string) => {
  const fetchApprovals = useCallback(
    async (status?: string) => {
      try {
        let query = supabase
          .from("approval_requests")
          .select(
            `
            id,
            project_id,
            contractor_id,
            timestamp,
            title,
            description,
            type,
            status,
            timeline_impact,
            priority,
            photo_urls,
            client_response,
            client_response_date,
            approval_line_items (
              id,
              description,
              quantity,
              unit_price
            )
          `
          )
          .eq("project_id", projectId);

        if (status) {
          query = query.eq("status", status);
        }

        const { data, error } = await query.order("timestamp", {
          ascending: false,
        });

        if (error) {
          console.error("Error fetching approvals:", error);
          return [];
        }

        // Map approval_line_items to pricing_breakdown and transform data
        const transformedData: Approval[] = (data || []).map((item: any) => ({
          ...item,
          pricing_breakdown: item.approval_line_items || null,
        }));

        return transformedData;
      } catch (err) {
        console.error("Error in fetchApprovals:", err);
        return [];
      }
    },
    [projectId]
  );

  const saveApproval = useCallback(
    async (formData: ApprovalFormData) => {
      try {
        const approval = {
          project_id: projectId,
          contractor_id: contractorId,
          title: formData.title,
          description: formData.description || null,
          type: formData.type,
          status: "pending" as const,
          timeline_impact: formData.timelineImpact
            ? parseInt(formData.timelineImpact)
            : null,
          priority: formData.priority,
          photo_urls: [],
        };

        const { data: approvalData, error: approvalError } = await supabase
          .from("approval_requests")
          .insert([approval])
          .select();

        if (approvalError) {
          console.error("Error creating approval:", approvalError);
          console.error(
            "Error details:",
            approvalError.message,
            approvalError.code
          );
          return null;
        }

        if (!approvalData || approvalData.length === 0) {
          console.error("No data returned after approval insert");
          return null;
        }

        const approvalId = approvalData[0].id;

        if (approvalId && formData.lineItems.length > 0) {
          const lineItems = formData.lineItems.map((item: any) => ({
            approval_id: approvalId,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unit_price,
          }));

          const { error: lineError } = await supabase
            .from("approval_line_items")
            .insert(lineItems);

          if (lineError) {
            console.error("Error creating line items:", lineError);
            console.error(
              "Line error details:",
              lineError.message,
              lineError.code
            );
            // Note: approval was created but line items failed
            return approvalData[0];
          }
        }

        // Notify the client about the new approval request
        const { data: projectData } = await supabase
          .from("projects")
          .select("client_id, title")
          .eq("id", projectId)
          .single();

        if (projectData?.client_id) {
          notify({
            recipientId: projectData.client_id,
            projectId,
            type: "approval_submitted",
            title: "New approval request",
            message: `Your contractor submitted an approval request: "${approval.title}"`,
            link: `/client/projects/${projectId}`,
          });
        }

        return approvalData[0] || null;
      } catch (err) {
        console.error("Error in saveApproval:", err);
        return null;
      }
    },
    [projectId, contractorId]
  );

  const updateApprovalStatus = useCallback(
    async (approvalId: string, status: string, clientResponse?: string) => {
      try {
        const updateData: Record<string, unknown> = { status };
        if (clientResponse) {
          updateData.client_response = clientResponse;
          updateData.client_response_date = new Date().toISOString();
        }

        const { data, error } = await supabase
          .from("approval_requests")
          .update(updateData)
          .eq("id", approvalId)
          .select();

        if (error) {
          console.error("Error updating approval:", error);
          return null;
        }

        return data?.[0] || null;
      } catch (err) {
        console.error("Error in updateApprovalStatus:", err);
        return null;
      }
    },
    []
  );

  const updateApproval = useCallback(
    async (approvalId: string, formData: ApprovalFormData) => {
      try {
        console.log("Starting approval update for ID:", approvalId);

        const updateData = {
          title: formData.title,
          description: formData.description || null,
          type: formData.type,
          timeline_impact: formData.timelineImpact
            ? parseInt(formData.timelineImpact)
            : null,
          priority: formData.priority,
        };

        const { data: approvalData, error: approvalError } = await supabase
          .from("approval_requests")
          .update(updateData)
          .eq("id", approvalId)
          .select();

        if (approvalError) {
          console.error("Error updating approval:", approvalError);
          return null;
        }

        console.log("Approval updated, now updating line items");

        // First, delete ALL old line items for this approval
        const { error: deleteError } = await supabase
          .from("approval_line_items")
          .delete()
          .eq("approval_id", approvalId);

        if (deleteError) {
          console.error("Error deleting old line items:", deleteError);
          // Continue anyway - we'll try to insert
        } else {
          console.log("Old line items deleted successfully");
        }

        // Wait a moment to ensure database consistency
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Insert fresh new line items
        if (formData.lineItems.length > 0) {
          const lineItems = formData.lineItems.map((item: any) => {
            // Always exclude id field to force fresh insertion
            return {
              approval_id: approvalId,
              description: item.description || "",
              quantity: Number(item.quantity),
              unit_price: Number(item.unit_price),
            };
          });

          console.log("Inserting new line items:", lineItems);

          const { data: insertedItems, error: lineError } = await supabase
            .from("approval_line_items")
            .insert(lineItems)
            .select();

          if (lineError) {
            console.error("Error inserting new line items:", lineError);
            return approvalData?.[0] || null;
          }

          console.log("Line items inserted successfully:", insertedItems);
        }

        console.log("Approval update complete");
        return approvalData?.[0] || null;
      } catch (err) {
        console.error("Error in updateApproval:", err);
        return null;
      }
    },
    []
  );

  const deleteApproval = useCallback(async (approvalId: string) => {
    try {
      // Delete line items first
      await supabase
        .from("approval_line_items")
        .delete()
        .eq("approval_id", approvalId);

      // Then delete the approval
      const { data, error } = await supabase
        .from("approval_requests")
        .delete()
        .eq("id", approvalId)
        .select();

      if (error) {
        console.error("Error deleting approval:", error);
        return false;
      }

      return !!data?.length;
    } catch (err) {
      console.error("Error in deleteApproval:", err);
      return false;
    }
  }, []);

  return {
    fetchApprovals,
    saveApproval,
    updateApproval,
    updateApprovalStatus,
    deleteApproval,
  };
};
